import { apiClient, apiGet, apiPost, apiPut } from '../lib/api-client';

export interface RepairInvoice {
  id: number;
  invoiceNumber: string;
  labour: number;
  parts: number;
  service: number;
  subtotal: number;
  taxRate: number;
  tax: number;
  total: number;
  approvalStatus: 'PENDING' | 'APPROVED' | 'DECLINED';
  invoiceStatus: 'DRAFT' | 'ISSUED' | 'PARTIALLY_PAID' | 'PAID' | 'VOID';
  paidAmount: number;
  balance: number;
  approvedAt?: string | null;
  issuedAt?: string | null;
}

export interface Repair {
  id: number;
  repairNumber: string;
  userId: number;
  deviceCategoryId: number;
  deviceBrandId: number;
  deviceModelId: number;
  devicePassword?: string;
  imei?: string;
  serialNumber?: string;
  faults: string[];
  description?: string;
  photos?: string[];
  status: 'SUBMITTED' | 'IN_PROGRESS' | 'WAITING_PARTS' | 'COMPLETED' | 'DELIVERED' | 'CLOSED';
  technicianId?: number;
  estimatedCost?: number;
  actualCost?: number;
  estimatedCompletion?: string;
  completedAt?: string;
  warrantyExpiry?: string;
  addressId?: number;
  pickupRequired: boolean;
  preferredDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  invoice?: RepairInvoice | null;
}

export interface CreateRepairData {
  deviceCategoryId: number;
  deviceBrandId: number;
  deviceModelId: number;
  devicePassword?: string;
  imei?: string;
  serialNumber?: string;
  faults: string[];
  description?: string;
  photos?: string[];
  addressId?: number;
  pickupRequired: boolean;
  preferredDate?: string;
  notes?: string;
}

export interface RepairStatusHistory {
  id: number;
  repairId: number;
  status: string;
  notes?: string;
  userId?: number;
  createdAt: string;
}

export const repairService = {
  async create(data: CreateRepairData): Promise<Repair> {
    return apiPost<Repair>('/repairs', data);
  },

  async getMyRepairs(): Promise<Repair[]> {
    return apiGet<Repair[]>('/repairs');
  },

  async getRepair(id: number): Promise<Repair> {
    return apiGet<Repair>(`/repairs/${id}`);
  },

  async getStatusHistory(id: number): Promise<RepairStatusHistory[]> {
    return apiGet<RepairStatusHistory[]>(`/repairs/${id}/history`);
  },

  async updateStatus(id: number, status: string, notes?: string): Promise<Repair> {
    return apiPut<Repair>(`/repairs/${id}/status`, { status, notes });
  },

  async assignTechnician(id: number, technicianId: number): Promise<Repair> {
    return apiPut<Repair>(`/repairs/${id}/assign`, { technicianId });
  },
  async approveEstimate(id: number): Promise<RepairInvoice> {
    return apiPost<RepairInvoice>(`/repairs/${id}/invoice/approve`);
  },
  async payInvoice(id: number, amount: number, method: 'paystack_card' | 'mobile_money' | 'bank_transfer' | 'cash', idempotencyKey?: string):
    Promise<{ payment: { id: number; reference: string; status: string }; paymentUrl?: string; instructions?: string; status?: 'pending' | 'success' }> {
    return apiPost(`/repairs/${id}/invoice/pay`, { amount, method, idempotencyKey });
  },
  async verifyInvoicePayment(id: number, reference: string): Promise<{ status: 'success' | 'pending' | 'failed'; payment?: { id: number; reference: string; status: string } }> {
    return apiPost(`/repairs/${id}/invoice/verify`, { reference });
  },
  async downloadInvoice(id: number): Promise<Blob> {
    const response = await apiClient.get(`/repairs/${id}/invoice/pdf`, { responseType: 'blob' });
    return response.data;
  },
};